import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// App-level layout state. This used to live in the redux `general` slice
// (`state.general.sidebar` / `.screenWidth`); it is pure client-side UI state,
// so it now lives in a React context mounted around <App />.
type LayoutContextValue = {
  sidebar: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  screenWidth: number;
  setScreenWidth: (width: number) => void;
};

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export const LayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [sidebar, setSidebar] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const toggleSidebar = useCallback(() => setSidebar(prev => !prev), []);
  const openSidebar = useCallback(() => setSidebar(true), []);
  const closeSidebar = useCallback(() => setSidebar(false), []);

  const value = useMemo<LayoutContextValue>(
    () => ({
      sidebar,
      toggleSidebar,
      openSidebar,
      closeSidebar,
      screenWidth,
      setScreenWidth,
    }),
    [sidebar, toggleSidebar, openSidebar, closeSidebar, screenWidth]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayoutContext = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayoutContext must be used within a LayoutProvider');
  }
  return context;
};
